import backendCall from '../../services/AxiosService';

/**
 * Generates and downloads a PDF for a clip installation event
 * @param eventId - The ID of the clip installation event
 * @returns Promise that resolves when the PDF has been opened or downloaded
 */
export const generateClipInstallPdf = async (eventId: string | number): Promise<void> => {
  return generateClipEventPdf(eventId, 'install');
};

/**
 * Generates and downloads a PDF for a clip removal event
 * @param eventId - The ID of the clip removal event
 * @returns Promise that resolves when the PDF has been opened or downloaded
 */
export const generateClipRemovalPdf = async (eventId: string | number): Promise<void> => {
  return generateClipEventPdf(eventId, 'removal');
};

/**
 * Generates and downloads a PDF for a clip pretrip event
 * @param pretripId - The ID of the clip pretrip event
 * @returns Promise that resolves when the PDF has been opened or downloaded
 */
export const generateClipPretripPdf = async (pretripId: string | number): Promise<void> => {
  try {
    // Generate the pretrip PDF
    
    // Call the backend API to generate the PDF
    const response = await backendCall.get(
      `/clip-ons/pdf/pretrip/${pretripId}`, 
      { responseType: 'blob' }
    );
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    let filename = `clip-pretrip-ticket.pdf`; // default filename
    
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '');
    }
    
    // Create a Blob URL
    const blobUrl = URL.createObjectURL(response.data);
    
    // Open in new tab
    const link = document.createElement('a');
    link.href = blobUrl;
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(blobUrl);
    
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Common function to generate clip event PDFs
 * @param eventId - The ID of the clip event
 * @param eventType - The type of event ('install' or 'removal')
 * @returns Promise that resolves when the PDF has been opened or downloaded
 */
const generateClipEventPdf = async (
  eventId: string | number, 
  eventType: 'install' | 'removal'
): Promise<void> => {
  // Generate the PDF for the clip event
  try {
    // Call the backend API to generate the PDF
    const response = await backendCall.get(
      `/clip-ons/pdf/${eventType}/${eventId}`, 
      { responseType: 'blob' }
    );
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    let filename = `clip-${eventType}-ticket.pdf`; // default filename
    
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '');
    }
    
    // Create a Blob URL
    const blobUrl = URL.createObjectURL(response.data);
    
    // Open in new tab
    const link = document.createElement('a');
    link.href = blobUrl;
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(blobUrl);
    
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};